/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { GridReadyEvent } from '@ag-grid-community/core';
import { ApplicationRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AgFns, GridOptionsExt, ISortModel, UtilDialogService } from '@core';
import {
  AccountPurchaseOrder,
  ApprovalTree,
  CollectionScope,
  CollectionScopeEnum,
  ProgramType,
  Program,
  ProgramStartDateType,
  ProgramStartDateTypeEnum,
  ActiveStatus,
  ShipFrequency,
  ProgramPurchaseOrderType,
  ProgramPcardType,
  AccountProcurementCard,
  ActiveStatusEnum,
  ProgramPcardTypeEnum,
  ProgramPurchaseOrderTypeEnum,
  ProgramSupplier,
  ProgramTypeEnum,
} from '@models';
import { DateFns, UtilFns } from '@utils';
import { DialogService } from 'primeng/dynamicdialog';
import { ProgramFrmComponent } from './program-frm.component';
import * as _ from 'lodash';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { Entity } from 'breeze-client';

import { AccountDbSaveService } from '../services/account-db-save.service';
import { OrgTreeDesignationService } from '../services/org-tree-designation.service';
import { RadioButtonClickEvent } from 'primeng/radiobutton';
import { IProgramCycleParams, IProgramNewCycleData, ProgramCycleDialogComponent } from './program-cycle.dialog.component';
// TODO: Replace with the correct import path if IProgramCycleParams is defined elsewhere

@Component({
  selector: 'prox-program-info-sub',
  templateUrl: './program-info.sub.component.html',
  standalone: false,
})
export class ProgramInfoSubComponent implements OnInit, OnChanges {
  ProgramStartDateTypeEnum = ProgramStartDateTypeEnum;
  ProgramTypeEnum = ProgramTypeEnum;
  CollectionScopeEnum = CollectionScopeEnum;

  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  // lookup types
  activeStatuses!: ActiveStatus[];
  programTypes!: ProgramType[];
  programStartDateTypes!: ProgramStartDateType[];
  shipFrequencies!: ShipFrequency[];

  // accountAdminGroups: AccountAdminGroup[] = [];
  approvalTrees!: ApprovalTree[];
  //currentApprovalTreeId = UtilFns.EmptyGuid;
  currentApprovalTreeId = null as any;
  programs!: Program[];
  availableNextPrograms!: Program[];
  isExpired = false;
  programPurchaseOrderTypes: ProgramPurchaseOrderType[] = [];
  programPcardTypes: ProgramPcardType[] = [];
  pcards!: AccountProcurementCard[];
  accountPos: AccountPurchaseOrder[] = [];
  pcardMessage = '';
  poMessage = '';
  collectionScopes: CollectionScope[] = [];
  collectionReleasedMessage = '';

  /**
   * Tree picker view-model. Program-specific trees already owned by ANOTHER current Program are
   * shown disabled and annotated with the owner's name rather than filtered out — an admin who
   * cannot find an expected tree raises a ticket, one who can see why does not.
   * See Docs/Program-Specific-Org-Trees.md §5a.
   */
  approvalTreeOptions: { id: string; name: string; disabled: boolean }[] = [];
  private designatedTreeOwners: { ApprovalTreeId: string; ProgramId: string; ProgramName: string }[] = [];
  displayDialog = false;

  // --- Managed-Distribution linkage grid (visible only on Managed Inventory programs) ---
  // Filtered view (supplier-account opted in to inventory) of parent.mdSuppliersForInventory.
  // Backing array is on the parent so mutations survive sub-component visibility toggles
  // and are visible to the parent's canActivate() check.
  mdSuppliers: ProgramSupplier[] = [];
  mdSuppliersGridOptions: GridOptionsExt = {};

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    public pngDialogService: DialogService,
    public designationService: OrgTreeDesignationService,
    private appRef: ApplicationRef,
  ) {}

  get program() {
    return this.parent.program;
  }

  get manifests() {
    return this.program?.programBillingConfigs.filter((x) => x.manifestId != null).map((x) => x.manifest);
  }

  get helpfulHints(): string[] {
    const hints: string[] = [];

    if (this.program.programTypeId === ProgramTypeEnum.Inventory) {
      hints.push('Your suppliers must pre-approve inventory items before they can be added to your managed inventory program.');
    }

    if (this.isIssuanceOnlyWithUntaggedProducts()) {
      hints.push('Every product for your issuance program must have at least one product tag.');
    }

    return hints;
  }

  private isIssuanceOnlyWithUntaggedProducts(): boolean {
    if (this.program.programTypeId === ProgramTypeEnum.Inventory) return false;
    if (!this.program.approvalTree?.approvalTreeUserGroups?.length) return false;

    const pugsWithoutAllowances = this.parent.getProgramUserGroupsWithoutAllowances();
    if (pugsWithoutAllowances.length === 0) return false;

    return this.program.programProductTypeConfigs.some((x) => x.productTypeConfig?.pricedProductType?.programProductTypeTagsDescr === '');
  }

  get descriptionPlaceholder(): string {
    const accountName = this.parent?.program?.account?.name || 'your organization';
    return `Enter a clear and concise description of this Proximity Program. Which are the staff, groups or teams at ${accountName} for whom this Program is intended? Is this Program exclusively for NEW hires, EXISTING staff or both? Is this an ongoing or a temporary, seasonal or emergency-use program?`;
  }

  async ngOnInit() {
    // Build gridOptions synchronously BEFORE any await — ag-grid-angular reads [gridOptions]
    // at construction time only. If we assign after the awaits below, ag-grid latches onto
    // the initial {} and our callbacks never fire.
    this.mdSuppliersGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onMdSuppliersGridReady,
      rowModelType: 'clientSide',
      getRowId: (params) => params.data.id,
    });

    const [r0, r1, r2, r3] = await Promise.all([
      this.dbQueryService.getAll(ActiveStatus),
      this.dbQueryService.getAll(ProgramType),
      this.dbQueryService.getAll(ProgramStartDateType),
      this.dbQueryService.getAll(ShipFrequency),
    ]);

    this.activeStatuses = r0;
    this.programTypes = r1;

    this.programStartDateTypes = r2;
    this.shipFrequencies = r3;

    const [p0, approvalTrees, programs, pos, p4] = await Promise.all([
      this.dbQueryService.getAccountAdminGroups(this.program.accountId),
      this.dbQueryService.getApprovalTrees(this.program.accountId),
      this.dbQueryService.getPrograms(this.program.accountId),
      this.dbQueryService.getAccountPurchaseOrders(this.program.accountId),
      this.dbQueryService.getAccountProcurementCards(this.program.accountId),
    ]);

    // this.accountAdminGroups = p0;
    this.approvalTrees = approvalTrees;
    this.programs = programs;
    this.accountPos = pos.filter((x) => !x.isParent());

    this.pcards = p4;

    // this.approvalTrees.unshift({
    //   id: null as any,
    //   name: '- None -',
    // } as ApprovalTree);

    this.isExpired = <Date>this.program.endDate < new Date();
    this.currentApprovalTreeId = this.program.approvalTreeId;

    this.programPurchaseOrderTypes = this.dbQueryService.getAllCached(ProgramPurchaseOrderType);
    this.programPcardTypes = this.dbQueryService.getAllCached(ProgramPcardType);
    this.collectionScopes = this.dbQueryService.getAllCached(CollectionScope);

    this.designatedTreeOwners = await this.dbQueryService.getDesignatedTreeOwners(this.program.accountId);
    this.buildApprovalTreeOptions();

    this.onPcardChanged();
    this.onPoChanged();
  }

  /** Owner of a program-specific tree, when it is someone OTHER than this program. */
  private otherOwnerOf(approvalTreeId?: string) {
    if (!approvalTreeId) return undefined;
    const owner = this.designatedTreeOwners.find((o) => o.ApprovalTreeId === approvalTreeId);
    return owner && owner.ProgramId !== this.program.id ? owner : undefined;
  }

  private buildApprovalTreeOptions() {
    this.approvalTreeOptions = (this.approvalTrees ?? []).map((at) => {
      const owner = this.otherOwnerOf(at.id);
      return {
        id: at.id,
        name: owner ? `${at.name}  —  in use by ${owner.ProgramName}` : at.name,
        // Never disable the tree this program is already on, even if the data says otherwise —
        // that would strand the program with an unselectable current value.
        disabled: !!owner && at.id !== this.program.approvalTreeId,
      };
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.parent.updateComponentNameMap(this);
    this.refresh();
  }

  onProgramTypeChange(e) {
    this.parent.buildMainMenu();
  }

  async refresh() {
    this.onPcardChanged();
    this.onPoChanged();

    if (this.program?.programTypeId === ProgramTypeEnum.Inventory) {
      await this.loadMdSuppliers();
    }
  }

  async onProgramApplicabilityTypeChanged(programApplicabilityTypeId: string) {
    // do nothing yet
  }

  async onProgramStartDateTypeChanged(programStartDateTypeId: string) {
    // do nothing yet
  }

  showAccessibilityInfo() {
    return this.program.programTypeId === ProgramTypeEnum.Managed;
  }

  async onApprovalTreeChanged(approvalTreeId: string) {
    const newAt = this.approvalTrees.find((at) => at.id == approvalTreeId);

    UtilFns.assertNonNull(newAt, 'Proximity Account Organization should have been found (BUG)');
    if (!newAt.id) return;

    // Guard 2. A program-specific tree serves ONE program, because only such a tree may carry
    // per-PUG purchase orders / p-cards / ordering time frames — settings resolved at order time
    // by (ProgramUserGroup x Tree), not by program. Attaching a second program here is exactly how
    // one program's purchase orders get silently re-routed to another's.
    // The picker already shows these disabled; this catches any other route in.
    const owner = this.otherOwnerOf(newAt.id);
    if (owner) {
      await this.dialogService.okDialog(
        'Cannot Use This Proximity Organization Tree',
        `<b>${newAt.name}</b> is a program-specific tree and is already in use by <b>${owner.ProgramName}</b>.` +
          `<br/><br/>A program-specific tree serves a single Program, because its Program User Group settings — ` +
          `purchase orders, procurement cards and ordering time frames — belong to that Program alone. ` +
          `Sharing it would let changes made for one Program silently re-route the other's orders.` +
          `<br/><br/>Choose a different tree, or give this Program its own copy.`,
      );
      this.currentApprovalTreeId = <string>this.program.approvalTreeId;
      return;
    }
    this.approvalTrees = this.approvalTrees.filter((x) => x.id != UtilFns.EmptyGuid);
    const treeUserGroups = _.flatten(newAt.approvalTreeAdminGroups.map((x) => x.approvalTreeUserGroups.map((y) => y.programUserGroup)));
    const treeUgIdSet = new Set(treeUserGroups.map((ug) => ug.id));

    const paugs = _.flatten(
      this.program.programAllowances.map((pa) => pa.programAllowanceUserGroupMaps.filter((x) => !treeUgIdSet.has(x.programUserGroupId))),
    );
    if (paugs.length > 0) {
      const errs = paugs
        .map((x) => `Program Allowance: '${x.programAllowance.name}' - User Group: '${x.programUserGroup.name}'`)
        .join('<br/>');
      await this.dialogService.okDialog(
        'Cannot Change Proximity Account Organization',
        `This Account Organization does not contain the following user groups that are referenced within this program </br></br> ${errs} `,
      );
      // Needed if we use a toast instead
      // this.appRef.tick();
      this.currentApprovalTreeId = this.program.approvalTreeId;
      return;
    }
    const piugs = _.flatten(
      this.program.programIssuances.map((pa) => pa.programIssuanceUserGroupMaps.filter((x) => !treeUgIdSet.has(x.programUserGroupId))),
    );
    if (piugs.length > 0) {
      const errs = piugs
        .map((x) => `Program Issuance: '${x.programIssuance.name}' - User Group: '${x.programUserGroup.name}'`)
        .join('<br/>');
      await this.dialogService.okDialog(
        'Cannot change Proximity Account Organization Tree',
        `This Account Organization Tree does not contain the following user groups that are referenced within this program </br></br> ${errs} `,
      );
      // Needed if we use a toast instead
      this.appRef.tick();
      this.currentApprovalTreeId = <string>this.program.approvalTreeId;
      return;
    }

    this.program.approvalTree = newAt;
    // TODO: review if needed.
    await this.dbQueryService.getProgramUserGroupsForApprovalTree(<string>this.program.approvalTreeId);
  }

  /** ATUGs the PO/p-card assignment warnings should consider. Excludes any ATUG whose
   *  own status or whose underlying PUG is Inactive — those rows are hidden from the
   *  Program User Groups grid by default and are typically consolidated leftovers that
   *  never had a PO/p-card assigned. Hold + Phaseout stay in scope so that a soon-to-be-
   *  reactivated PUG missing a PO still warns. Mirrors the grid's default filter (see
   *  program-user-groups.sub.component.ts applyShowInactiveFilter). */
  private assignableAtugs() {
    return (this.program.approvalTree?.approvalTreeUserGroups ?? []).filter(
      (a) =>
        a.activeStatusId !== ActiveStatusEnum.Inactive &&
        a.programUserGroup?.activeStatusId !== ActiveStatusEnum.Inactive,
    );
  }

  /**
   * Explains what the chosen scope means in terms of THIS program's configuration, rather than
   * making the admin infer it from the enum description.
   */
  /** Sub-line for the Order Flow badge — which release grouping this campaign uses. */
  get collectionScopeBadge(): string {
    if (this.program.collectionScopeId === CollectionScopeEnum.ProgramWide) {
      return this.program.earliestPlacementDate
        ? `Whole program, no earlier than ${DateFns.fmtDate(this.program.earliestPlacementDate)}`
        : 'Whole program — no placement date set';
    }
    return 'One batch per program user group';
  }

  get collectionScopeHint(): string {
    if (!this.program.collectAndPlace) return '';
    if (this.program.collectionScopeId === CollectionScopeEnum.ProgramWide) {
      return (
        'One batch for the whole program. Every collected order is reviewed and placed together, and the ' +
        'Earliest Placement Date above is the date published to the organization.'
      );
    }
    const atugs = this.assignableAtugs();
    const missing = atugs.filter((a) => a.orderTimeFrameStartDays == null || a.orderTimeFrameDurationDays == null);
    const base =
      'One batch per program user group, each released on its own. Each group’s placement date is the end of its ' +
      'Restricted Ordering Time Frame, set on the Proximity Organization page.';
    return missing.length === 0
      ? base
      : `${base} ${missing.length} of ${atugs.length} group(s) have no time frame set — the program cannot be activated until they do.`;
  }

  /**
   * Collection is an episode, not a cycle: releasing a batch stamps the released timestamps and turns
   * CollectAndPlace back off. Re-enabling it therefore starts a NEW campaign, so the stamps from the
   * previous one have to be cleared or every order would route straight past collection.
   */
  async onCollectAndPlaceChanged() {
    this.collectionReleasedMessage = '';

    // Turning collection OFF while orders are collected would strand them: AwaitingRelease is only
    // reachable onward through the release action, and that action only exists while the Program is
    // collecting. Refuse and say how many are waiting.
    if (!this.program.collectAndPlace) {
      const collected = await this.dbQueryService.getCollectedOrderCountForProgram(this.program.id);
      if (collected > 0) {
        this.program.collectAndPlace = true;
        await this.dialogService.okDialog(
          'Cannot Turn Off Collection Yet',
          `This Program has <b>${collected}</b> collected order${collected === 1 ? '' : 's'} waiting to be released.` +
            `<br/><br/>Turning collection off now would strand ${collected === 1 ? 'it' : 'them'} — a collected order can ` +
            `only reach a supplier through the release action, and that action exists only while the Program is collecting.` +
            `<br/><br/>Release the batch first, then turn this off.`,
        );
        return;
      }
      return;
    }

    const staleAtugs = this.assignableAtugs().filter((a) => a.collectionReleasedTs != null);
    const wasReleased = this.program.collectionReleasedTs != null || staleAtugs.length > 0;

    this.program.collectionReleasedTs = undefined;
    staleAtugs.forEach((a) => (a.collectionReleasedTs = undefined));

    // Switching collection on opens an episode, and the episode needs a beginning: while a program
    // is collecting, each Account User may place ONE order dated on or after this. Defaults to
    // today and stays editable, because an administrator setting up on Tuesday for a collection
    // that opened on Monday should be able to say so. Re-opening for a later episode in the same
    // program term sets a fresh date, which is what keeps the two episodes apart.
    this.program.collectionStartDate = DateFns.today();

    if (wasReleased) {
      this.collectionReleasedMessage =
        'A previous collection for this program was already released. Starting a new collection — orders approved from now ' +
        'on will be held for review again. Each user may place one order from the new collection start date.';
    }

    // Promote-on-demand, TRIGGER 2. Collection at ANY scope needs a dedicated tree: the tree
    // defines which groups are in the batch, and a shared one can be restructured mid-campaign.
    await this.offerDesignationForPerPug(this.designationService.reasonForCollection());
  }

  /**
   * Changing scope re-groups collected orders under a different release boundary — Program-wide
   * releases as one batch, PUG-wide as one per group. Orders already collected under the previous
   * grouping have no coherent home, so the change is refused until the batch is released.
   */
  async onCollectionScopeChanged() {
    this.collectionReleasedMessage = '';
    const collected = await this.dbQueryService.getCollectedOrderCountForProgram(this.program.id);
    if (collected === 0) return;

    const original = this.program.entityAspect?.originalValues as Record<string, unknown> | undefined;
    const previous = original && 'collectionScopeId' in original ? (original['collectionScopeId'] as CollectionScopeEnum) : undefined;
    if (previous === undefined) return;

    this.program.collectionScopeId = previous;
    await this.dialogService.okDialog(
      'Cannot Change Collection Scope Yet',
      `This Program has <b>${collected}</b> collected order${collected === 1 ? '' : 's'} waiting to be released.` +
        `<br/><br/>Scope decides how collected orders are grouped for release — the whole Program at once, or each ` +
        `Program User Group on its own. Changing it now would re-group orders that were collected under the current ` +
        `setting.<br/><br/>Release the batch first, then change this.`,
    );
  }

  /**
   * Promote-on-demand, TRIGGER 1 — the earliest and cheapest moment.
   *
   * Choosing "Per Program User Group" IS the administrator declaring that these settings belong to
   * this Program alone, and it happens before any group settings exist. Promoting here costs
   * nothing; promoting later, from the group dialog, means the Program is already built out and
   * the tree may already be shared.
   *
   * Only fires on a real user click. Both handlers are also called on load and on refresh to
   * recompute their warning text, and prompting then would ambush someone who merely opened the
   * page — hence the callers gate on the click event being present.
   *
   * The caller must also key off the CLICKED value, not program.programPurchaseOrderTypeId.
   * PrimeNG fires (onClick) BEFORE [(ngModel)] propagates, so at this point the model still holds
   * the PREVIOUS selection. Reading it here meant the prompt never fired on the click that
   * mattered, and the later event-less refresh — which does see the new value — correctly bailed
   * for having no event. Net effect: trigger 1 silently never ran.
   */
  private async offerDesignationForPerPug(reason: string) {
    const tree = this.program.approvalTree;
    if (!tree || tree.isProgramSpecific) return;

    const agreed = await this.designationService.offerToDesignate(tree, reason);
    if (!agreed) return;

    // Set it, do NOT save it here. The Program form's Save commits every pending change in the
    // unit of work, so the designation rides along with the purchase-order-type change that
    // prompted it — one user action, one commit, and Undo reverts both. Saving the tree
    // immediately left a designated tree behind whenever the administrator then hit Undo.
    tree.isProgramSpecific = true;
    this.buildApprovalTreeOptions();
  }

  /** When programPcardTypeId is changed. `clickedTypeId` is the value being selected — see
   *  offerDesignationForPerPug for why the model cannot be trusted at click time. */
  async onPcardChanged(evt?: RadioButtonClickEvent, clickedTypeId?: ProgramPcardTypeEnum) {
    this.pcardMessage = '';

    if (evt && clickedTypeId === ProgramPcardTypeEnum.ByProgramUserGroup) {
      await this.offerDesignationForPerPug(this.designationService.reasonForPerPugPcards());
    }

    if (this.program.programPcardTypeId == ProgramPcardTypeEnum.ByProgramUserGroup) {
      const tree = this.program.approvalTree;
      if (tree && !tree.isProgramSpecific) {
        this.pcardMessage =
          `Procurement cards cannot be assigned per program user group while '${tree.name}' is shared with other ` +
          `Programs. Dedicate it to this Program from the Proximity Organization page, or re-select this option to be asked again.`;
      } else if (this.assignableAtugs().some((p) => p.accountProcurementCardId == null)) {
        this.pcardMessage = 'Some program user groups do not have p-cards assigned';
      }
    }
  }

  /** When programPurchaseOrderTypeId is changed */
  async onPoChanged(evt?: RadioButtonClickEvent, clickedTypeId?: ProgramPurchaseOrderTypeEnum) {
    this.poMessage = '';

    if (evt && clickedTypeId === ProgramPurchaseOrderTypeEnum.ByProgramUserGroup) {
      await this.offerDesignationForPerPug(this.designationService.reasonForPerPugPurchaseOrders());
    }

    if (this.program.programPurchaseOrderTypeId == ProgramPurchaseOrderTypeEnum.ByProgramUserGroup) {
      const tree = this.program.approvalTree;
      if (tree && !tree.isProgramSpecific) {
        // The real blocker. Saying "some groups have no purchase orders" here would be misleading:
        // they CANNOT be assigned at all while the tree is shared, because those fields are
        // withheld on a shared tree. Name the actual obstacle and where to fix it.
        this.poMessage =
          `Purchase orders cannot be assigned per program user group while '${tree.name}' is shared with other ` +
          `Programs. Dedicate it to this Program from the Proximity Organization page, or re-select this option to be asked again.`;
      } else if (this.assignableAtugs().some((p) => p.accountPurchaseOrderId == null)) {
        this.poMessage = 'Some program user groups do not have purchase orders assigned';
      }
    }
    // clear the program's purchase order if the new type is not ByProgram since this the only type that allows a program-level purchase order
    if (this.program.programPurchaseOrderTypeId != ProgramPurchaseOrderTypeEnum.ByProgram) {
      this.program.accountPurchaseOrderId = undefined;
    }
  }

  async markError(errEnt: Entity, propName?: string) {
    UtilFns.focusInputByEntity('#topLevel', errEnt, propName || '');
  }

  cancelDialog() {
    this.parent.dbSaveService.rejectChanges();
  }

  statusName() {
    // return this.statusService.getWorkingStatus(this.program as any).shortDisplay;
    return this.program.activeStatus?.name;
  }

  isEditable() {
    if (this.parent.isBeingAdded) {
      return true;
    }
    return false;
  }

  statusButtonLabel() {
    /*       if (this.statusService.getWorkingStatus(this.program as any).maxStatusEntity.getStatusId() == ActiveStatusEnum.Active) { */
    if (this.program.activeStatusId == ActiveStatusEnum.Active) {
      return 'Place Program on Hold';
    } else if (this.program.activeStatusId == ActiveStatusEnum.Hold) {
      return 'Activate Program';
    }
    return 'Contact Principal Account Administrator';
  }

  allowCycle() {
    if (this.dbSaveService.hasChanges()) {
      return false;
    }

    return (
      this.program.programTypeId === ProgramTypeEnum.Managed &&
      (this.program.programStartDateTypeId === ProgramStartDateTypeEnum.FixedStartAndEndDate ||
        this.program.programStartDateTypeId === ProgramStartDateTypeEnum.VariableStartFixedEndDate)
    );
  }

  async onCycle() {
    const data: IProgramCycleParams = {} as IProgramCycleParams;
    data.oldProgram = this.program;

    const result = await ProgramCycleDialogComponent.openDialog(
      this.pngDialogService,
      {
        ...data,
      },
      {
        header: 'Cycle Program: ' + this.program.name + ' for Account: ' + this.program.account.name,
      },
    );

    if (!result) return;
    const newData = result as IProgramNewCycleData;

    await this.parent.onCopyProgram(newData);
    this.displayDialog = true;
    // await this.dialogService.okDialog(
    //   'Cycle Program Complete',
    //   'Cycled program: ' +
    //     newData.newProgramName +
    //     ' is ready to be edited and saved.  PLEASE NOTE: values for (1) Purchase Order Type (2) Purchase Order Required (3) Procurement Card Type and (4) Procurement Card Required have been set to default.',
    // );
    //this.toastr.success('Cycled program: ' + newData.newProgramName + ' is ready to be edited and saved', 'Cycle Program Complete');
  }

  hideDialog() {
    this.displayDialog = false;
  }

  // ---------- Managed-Distribution linkage grid ----------

  /**
   * Filter the parent's preloaded MD ProgramSupplier list down to just those whose
   * SupplierAccount opted in to inventory. Mutations to these entities (via Apply/Clear
   * below) also update parent.mdSuppliersForInventory since the array elements are the
   * same Breeze entity references — that's what keeps canActivate() in sync.
   */
  async loadMdSuppliers() {
    const suppliersWithInventoryAllowed = new Set(
      this.parent.account.supplierAccounts.filter((sa) => sa.allowInventory).map((sa) => sa.supplierId),
    );
    this.mdSuppliers = this.parent.mdSuppliersForInventory.filter((ps) => suppliersWithInventoryAllowed.has(ps.supplierId));
    await AgFns.refreshGrid(this.mdSuppliersGridOptions, this.mdSuppliers);
  }

  onMdSuppliersGridReady(event?: GridReadyEvent) {
    const readOnly = () => this.parent.isReadOnly();
    const thisProgramId = this.program.id;

    const applyCol = AgFns.createButtonProps('', this.onMdApply.bind(this), {
      label: 'Apply',
      style: 'text',
      calcDisabled: (ps: ProgramSupplier) =>
        readOnly() || (ps.allowInventory && ps.inventoryProgramId === thisProgramId),
      tooltip: 'Set this Managed Distribution program to draw from this Inventory Program',
    });
    const clearCol = AgFns.createButtonProps('', this.onMdClear.bind(this), {
      label: 'Clear',
      style: 'text',
      calcDisabled: (ps: ProgramSupplier) => readOnly() || !ps.allowInventory,
      tooltip: 'Stop this Managed Distribution program from drawing from an Inventory Program',
    });

    const colDefs = [
      { ...applyCol, ...AgFns.fixWidth(90) },
      { ...clearCol, ...AgFns.fixWidth(90) },
      {
        headerName: 'Supplier',
        field: 'supplier.name',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'Managed Distribution Program',
        field: 'program.name',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(220),
      },
      {
        ...AgFns.createCheckboxProps('Uses Managed Inventory?', 'allowInventory'),
        editable: false,
        ...AgFns.fixWidth(180),
      },
      {
        headerName: 'Inventory Program',
        valueGetter: (params) =>
          params.data.allowInventory ? params.data.inventoryProgram?.name ?? '' : '',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(220),
      },
    ];
    const sortModel: ISortModel = [
      { colId: 'supplier.name', sort: 'asc' },
      { colId: 'program.name', sort: 'asc' },
    ];
    AgFns.initGrid(event ?? this.mdSuppliersGridOptions, colDefs, sortModel);
  }

  async onMdApply(ps: ProgramSupplier) {
    if (this.parent.isReadOnly()) return;

    // Already applied to this Inventory Program — no-op (defensive; button is disabled).
    if (ps.allowInventory && ps.inventoryProgramId === this.program.id) return;

    // Applied to a different Inventory Program — confirm before redirecting.
    if (ps.allowInventory && ps.inventoryProgramId && ps.inventoryProgramId !== this.program.id) {
      const currentName = ps.inventoryProgram?.name ?? 'another Inventory Program';
      const ok = await this.dialogService.askIfOk(
        'Redirect Inventory Source',
        `'${ps.program.name}' currently draws from '${currentName}'. Redirect it to draw from this Inventory Program ('${this.program.name}')?`,
      );
      if (!ok) return;
    }

    ps.allowInventory = true;
    ps.inventoryProgramId = this.program.id;
    this.mdSuppliersGridOptions.apiX?.refreshCells({ force: true });
  }

  onMdClear(ps: ProgramSupplier) {
    if (this.parent.isReadOnly()) return;
    if (!ps.allowInventory) return;
    ps.allowInventory = false;
    ps.inventoryProgramId = undefined;
    this.mdSuppliersGridOptions.apiX?.refreshCells({ force: true });
  }
}
